import { useState, useEffect, useMemo } from "react";
import { X, Info, AlertTriangle, CheckCircle, Tag, ArrowRight } from "lucide-react";
import { trpc } from "@/lib/trpc";

const DISMISSED_KEY = "aspab-dismissed-banners";

function getDismissedBanners(): number[] {
  try {
    const stored = localStorage.getItem(DISMISSED_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

function dismissBanner(id: number) {
  const dismissed = getDismissedBanners();
  if (!dismissed.includes(id)) {
    dismissed.push(id);
    localStorage.setItem(DISMISSED_KEY, JSON.stringify(dismissed));
  }
}

const typeConfig = {
  info: {
    bg: "bg-[#2563EB]",
    text: "text-white",
    icon: Info,
    iconColor: "text-white/80",
    closeHover: "hover:bg-white/20",
  },
  warning: {
    bg: "bg-amber-500",
    text: "text-white",
    icon: AlertTriangle,
    iconColor: "text-white/80",
    closeHover: "hover:bg-white/20",
  },
  success: {
    bg: "bg-emerald-600",
    text: "text-white",
    icon: CheckCircle,
    iconColor: "text-white/80",
    closeHover: "hover:bg-white/20",
  },
  promotion: {
    bg: "bg-gradient-to-r from-[#1D4ED8] to-[#7C3AED]",
    text: "text-white",
    icon: Tag,
    iconColor: "text-white/80",
    closeHover: "hover:bg-white/20",
  },
};

export default function AnnouncementBanner() {
  const { data: banners, isLoading } = trpc.banners.getActive.useQuery(undefined, {
    refetchOnWindowFocus: false,
    staleTime: 60_000, // Cache for 1 minute
    retry: false,
  });

  const [dismissedIds, setDismissedIds] = useState<number[]>(() =>
    getDismissedBanners()
  );

  const visibleBanners = useMemo(() => {
    if (!banners) return [];
    return banners.filter((b) => !dismissedIds.includes(b.id));
  }, [banners, dismissedIds]);

  const handleDismiss = (id: number) => {
    dismissBanner(id);
    setDismissedIds((prev) => [...prev, id]);
  };

  if (isLoading || visibleBanners.length === 0) return null;

  return (
    <>
      {visibleBanners.map((banner) => {
        const config = typeConfig[banner.type as keyof typeof typeConfig] || typeConfig.info;
        const Icon = config.icon;

        return (
          <div
            key={banner.id}
            className={`${config.bg} ${config.text} relative z-50`}
            role="alert"
            aria-live="polite"
          >
            <div className="container py-2.5 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 min-w-0">
                <Icon className={`w-4 h-4 ${config.iconColor} shrink-0`} />
                <div className="flex items-center gap-2 min-w-0 flex-wrap">
                  {banner.title && (
                    <span className="font-semibold text-sm whitespace-nowrap">
                      {banner.title}
                    </span>
                  )}
                  <span className="text-sm opacity-90 truncate">
                    {banner.message}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                {banner.linkUrl && banner.linkText && (
                  <a
                    href={banner.linkUrl}
                    className="inline-flex items-center gap-1 text-sm font-medium underline underline-offset-2 hover:no-underline whitespace-nowrap"
                  >
                    {banner.linkText}
                    <ArrowRight className="w-3 h-3" />
                  </a>
                )}
                {banner.dismissible && (
                  <button
                    onClick={() => handleDismiss(banner.id)}
                    className={`p-1 rounded-md ${config.closeHover} transition-colors`}
                    aria-label="Stäng meddelande"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </>
  );
}
